// ============================================================
// ACCOMMODATION FORM - STEP VALIDATION
// ------------------------------------------------------------
// Pulled out of AccommodationForm.jsx. Pure functions only -
// no component state, just (data, steps) in and an errors
// object out - so this can be unit tested on its own.
// ============================================================

import { steps as defaultSteps } from './accommodationFormConfig';

const toNumber = (value, fallback = 0) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
};

/* ============================================================
   VALIDATE ONE STEP
============================================================ */

export function validateStep(stepIndex, data, steps = defaultSteps) {
    const stepId = steps[stepIndex]?.id;

    const newErrors = {};

    // =================================================
    // BASIC
    // =================================================

    if (stepId === 'basic') {
        if (!data.title?.trim()) {
            newErrors.title = 'Property name is required';
        }

        if (!data.propertyType) {
            newErrors.propertyType = 'Property type is required';
        }

        if (!data.propertyCategory) {
            newErrors.propertyCategory = 'Property category is required';
        }
    }

    // =================================================
    // LOCATION
    // =================================================

    if (stepId === 'location') {
        if (!data.address?.city?.trim()) {
            newErrors.city = 'City is required';
        }

        if (!data.address?.state?.trim()) {
            newErrors.state = 'State is required';
        }

        if (!data.address?.country?.trim()) {
            newErrors.country = 'Country is required';
        }

        const coordinates = data.address?.geo?.coordinates;

        const hasValidCoordinates =
            Array.isArray(coordinates) &&
            coordinates.length === 2 &&
            Number.isFinite(Number(coordinates[0])) &&
            Number.isFinite(Number(coordinates[1])) &&
            !(Number(coordinates[0]) === 0 && Number(coordinates[1]) === 0);

        if (!hasValidCoordinates) {
            newErrors.geo = 'Set the property location on the map';
        }
    }

    // =================================================
    // UNITS
    // =================================================

    if (stepId === 'units') {
        const units = Array.isArray(data.units) ? data.units : [];

        if (units.length === 0) {
            newErrors.units = 'At least one accommodation unit is required';
        } else {
            const unitErrors = {};

            units.forEach((unit, index) => {
                const unitError = {};

                // -------------------------------------
                // NAME
                // -------------------------------------

                if (!unit?.name?.trim()) {
                    unitError.name = 'Unit name is required';
                }

                // -------------------------------------
                // UNIT TYPE
                // -------------------------------------

                if (!unit?.unitType) {
                    unitError.unitType = 'Unit type is required';
                }

                // -------------------------------------
                // MAX GUESTS
                // -------------------------------------

                const maxGuests = toNumber(unit?.occupancy?.maxGuests, 0);

                if (maxGuests <= 0) {
                    unitError.maxGuests = 'Maximum guests must be greater than 0';
                }

                // -------------------------------------
                // INVENTORY
                // -------------------------------------

                const totalUnits = toNumber(unit?.inventory?.totalUnits, 0);

                const activeUnits = toNumber(unit?.inventory?.activeUnits, 0);

                const maintenanceUnits = toNumber(unit?.inventory?.maintenanceUnits, 0);

                if (totalUnits <= 0) {
                    unitError.totalUnits = 'Total units must be greater than 0';
                }

                if (activeUnits < 0) {
                    unitError.activeUnits = 'Active units cannot be negative';
                }

                if (maintenanceUnits < 0) {
                    unitError.maintenanceUnits = 'Maintenance units cannot be negative';
                }

                if (activeUnits > totalUnits) {
                    unitError.activeUnits = 'Active units cannot exceed total units';
                }

                if (maintenanceUnits > totalUnits) {
                    unitError.maintenanceUnits = 'Maintenance units cannot exceed total units';
                }

                if (activeUnits + maintenanceUnits > totalUnits) {
                    unitError.inventory = 'Active and maintenance units cannot exceed total units';
                }

                // -------------------------------------
                // PRICE
                // -------------------------------------

                const price = toNumber(unit?.pricing?.actual, 0);

                if (price <= 0) {
                    unitError.actual = 'Price must be greater than 0';
                }

                if (Object.keys(unitError).length) {
                    unitErrors[index] = unitError;
                }
            });

            if (Object.keys(unitErrors).length) {
                newErrors.unitErrors = unitErrors;

                newErrors.units = 'Please complete all required unit information';
            }
        }
    }

    // =================================================
    // AMENITIES
    // =================================================

    if (stepId === 'amenities') {
        // Optional.
    }

    // =================================================
    // PRICING
    // =================================================

    if (stepId === 'pricing') {
        const units = Array.isArray(data.units) ? data.units : [];

        if (units.length === 0) {
            newErrors.units = 'At least one accommodation unit is required';
        } else {
            const unitErrors = {};

            units.forEach((unit, index) => {
                const unitError = {};

                // -------------------------------------
                // PRICE
                // -------------------------------------

                const actualPrice = Number(unit?.pricing?.actual);

                if (!Number.isFinite(actualPrice) || actualPrice <= 0) {
                    unitError.actual = 'Price must be greater than 0';
                }

                // -------------------------------------
                // CURRENCY
                // -------------------------------------

                if (!unit?.pricing?.currency) {
                    unitError.currency = 'Currency is required';
                }

                // -------------------------------------
                // INVENTORY
                // -------------------------------------

                const totalUnits = toNumber(unit?.inventory?.totalUnits, 0);

                const activeUnits = toNumber(unit?.inventory?.activeUnits, 0);

                const maintenanceUnits = toNumber(unit?.inventory?.maintenanceUnits, 0);

                if (totalUnits <= 0) {
                    unitError.totalUnits = 'Total units must be greater than 0';
                }

                if (activeUnits < 0) {
                    unitError.activeUnits = 'Active units cannot be negative';
                }

                if (maintenanceUnits < 0) {
                    unitError.maintenanceUnits = 'Maintenance units cannot be negative';
                }

                if (activeUnits > totalUnits) {
                    unitError.activeUnits = 'Active units cannot exceed total units';
                }

                if (maintenanceUnits > totalUnits) {
                    unitError.maintenanceUnits = 'Maintenance units cannot exceed total units';
                }

                if (activeUnits + maintenanceUnits > totalUnits) {
                    unitError.inventory = 'Active and maintenance units cannot exceed total units';
                }

                if (Object.keys(unitError).length) {
                    unitErrors[index] = unitError;
                }
            });

            if (Object.keys(unitErrors).length) {
                newErrors.unitErrors = unitErrors;

                newErrors.pricing = 'Please complete pricing and availability for all units';
            }
        }

        // ---------------------------------------------
        // MINIMUM STAY
        // ---------------------------------------------

        const policies = data.policies || {};

        const minStayRaw = policies.minimumStay ?? policies.minStay;

        const maxStayRaw = policies.maximumStay ?? policies.maxStay;

        const minimumStay = Number(minStayRaw);

        const maximumStay = Number(maxStayRaw);

        if (minStayRaw !== undefined && minStayRaw !== '' && (!Number.isFinite(minimumStay) || minimumStay <= 0)) {
            newErrors.minimumStay = 'Minimum stay must be greater than 0';
        }

        // ---------------------------------------------
        // MAXIMUM STAY
        // ---------------------------------------------

        if (maxStayRaw !== undefined && maxStayRaw !== null && maxStayRaw !== '' && Number(maxStayRaw) !== 0) {
            if (!Number.isFinite(maximumStay) || maximumStay <= 0) {
                newErrors.maximumStay = 'Maximum stay must be greater than 0';
            }
        }

        // ---------------------------------------------
        // MIN <= MAX
        // ---------------------------------------------

        if (Number.isFinite(minimumStay) && Number.isFinite(maximumStay) && maximumStay > 0 && maximumStay < minimumStay) {
            newErrors.maximumStay = 'Maximum stay cannot be less than minimum stay';
        }
    }

    // =================================================
    // SERVICES
    // =================================================

    if (stepId === 'services') {
        const services = Array.isArray(data.services) ? data.services : [];

        const serviceErrors = {};

        services.forEach((service, index) => {
            const serviceError = {};

            if (!service?.name?.trim()) {
                serviceError.name = 'Service name is required';
            }

            if (service?.type === 'paid') {
                const price = Number(service.price);

                if (!Number.isFinite(price) || price <= 0) {
                    serviceError.price = 'Price must be greater than 0';
                }
            }

            if (Object.keys(serviceError).length) {
                serviceErrors[index] = serviceError;
            }
        });

        if (Object.keys(serviceErrors).length) {
            newErrors.serviceErrors = serviceErrors;

            newErrors.services = 'Please complete the added services';
        }
    }

    // =================================================
    // POLICIES
    // =================================================

    if (stepId === 'policies') {
        const policies = data.policies || {};

        const minStayRaw = policies.minimumStay ?? policies.minStay;

        const maxStayRaw = policies.maximumStay ?? policies.maxStay;

        const minimumStay = Number(minStayRaw);

        const maximumStay = Number(maxStayRaw);

        if (minStayRaw !== undefined && minStayRaw !== '' && (!Number.isFinite(minimumStay) || minimumStay <= 0)) {
            newErrors.minimumStay = 'Minimum stay must be greater than 0';
        }

        if (maxStayRaw !== undefined && maxStayRaw !== null && maxStayRaw !== '' && Number(maxStayRaw) !== 0) {
            if (!Number.isFinite(maximumStay) || maximumStay <= 0) {
                newErrors.maximumStay = 'Maximum stay must be greater than 0';
            }

            if (Number.isFinite(minimumStay) && Number.isFinite(maximumStay) && maximumStay < minimumStay) {
                newErrors.maximumStay = 'Maximum stay cannot be less than minimum stay';
            }
        }
    }

    // =================================================
    // MEDIA
    // =================================================

    if (stepId === 'media') {
        /*
         * Media remains optional.
         *
         * If you later decide that at least one image
         * is mandatory, add that rule here.
         */
    }

    // =================================================
    // ADDITIONAL
    // =================================================

    if (stepId === 'additional') {
        /*
         * IMPORTANT:
         *
         * nearbyPlaces and faqs are ROOT fields.
         *
         * They are NOT:
         *
         * data.additional.nearbyPlaces
         */

        const nearbyPlaces = Array.isArray(data.nearbyPlaces) ? data.nearbyPlaces : [];

        const nearbyErrors = {};

        nearbyPlaces.forEach((place, index) => {
            const placeError = {};

            if (!place?.name?.trim()) {
                placeError.name = 'Place name is required';
            }

            if (place?.distance !== undefined && place?.distance !== '' && Number(place.distance) < 0) {
                placeError.distance = 'Distance cannot be negative';
            }

            if (Object.keys(placeError).length) {
                nearbyErrors[index] = placeError;
            }
        });

        if (Object.keys(nearbyErrors).length) {
            newErrors.nearbyPlaces = nearbyErrors;
        }

        // ---------------------------------------------
        // FAQ
        // ---------------------------------------------

        const faqs = Array.isArray(data.faqs) ? data.faqs : [];

        const faqErrors = {};

        faqs.forEach((faq, index) => {
            const faqError = {};

            if (!faq?.question?.trim()) {
                faqError.question = 'Question is required';
            }

            if (!faq?.answer?.trim()) {
                faqError.answer = 'Answer is required';
            }

            if (Object.keys(faqError).length) {
                faqErrors[index] = faqError;
            }
        });

        if (Object.keys(faqErrors).length) {
            newErrors.faqs = faqErrors;
        }
    }

    // =================================================
    // REVIEW
    // =================================================

    if (stepId === 'review') {
        // Final validation is handled separately.
    }

    return newErrors;
}

/* ============================================================
   VALIDATE ALL STEPS
============================================================ */

export function validateAllSteps(formData, steps = defaultSteps) {
    const allErrors = {};

    let firstInvalidStep = null;

    steps.forEach((step, index) => {
        /*
         * Review has no fields.
         */
        if (step.id === 'review') {
            return;
        }

        const stepErrors = validateStep(index, formData, steps);

        if (Object.keys(stepErrors).length) {
            allErrors[step.id] = stepErrors;

            if (firstInvalidStep === null) {
                firstInvalidStep = index;
            }
        }
    });

    return {
        valid: Object.keys(allErrors).length === 0,

        errors: allErrors,

        firstInvalidStep
    };
}
